import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type } from "arktype";
import { ArrowLeft } from "lucide-react";

import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { PageContainer } from "@/components/ui/page-container";
import type { PersonDetail } from "@/api/db/people";
import { FICHA_FOCUS, FICHA_TABS, type FichaTab } from "@/lib/labels";
import { fallback, optional, pageParam, qParam, sourceParam } from "@/lib/search-params";

import { FichaCadastro } from "./-components/ficha-cadastro";
import { FichaConversation } from "./-components/ficha-conversation";
import { FichaHeader } from "./-components/ficha-header";
import { FichaSkeleton } from "./-components/ficha-skeleton";
import { FichaTabsBar } from "./-components/ficha-tabs";

const idParam = type("string.integer.parse | number.integer");

const paramsSchema = type({
	id: type("string | number").pipe((raw) => {
		const id = idParam(raw);

		if (id instanceof type.errors) {
			return null;
		}

		return id;
	}),
});

const searchSchema = type({
	tab: fallback(type.enumerated(...FICHA_TABS), "cadastro"),
	"focus?": optional(type.enumerated(...FICHA_FOCUS)),
	"q?": qParam,
	"source?": sourceParam,
	page: pageParam,
});

export const Route = createFileRoute("/_app/pessoa/$id")({
	params: {
		parse: (raw) => paramsSchema.assert(raw),
		stringify: ({ id }) => ({ id: String(id) }),
	},
	validateSearch: searchSchema,
	component: FichaPage,
});

function FichaPage() {
	const { id } = Route.useParams();
	const { tab, q, source, page } = Route.useSearch();

	return (
		<PageContainer className="mx-auto max-w-screen-2xl">
			<div className="mb-4">
				<Link
					to="/pessoas"
					search={{ q, source, page }}
					className="inline-flex items-center gap-2 rounded-full px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
				>
					<ArrowLeft className="size-4" />
					Voltar para pessoas
				</Link>
			</div>

			{id === null && <p className="text-sm text-muted-foreground">Pessoa não encontrada.</p>}

			{id !== null && (
				<ErrorBoundary fallback={<FichaError id={id} />}>
					<Suspense fallback={<FichaSkeleton />}>
						<FichaData id={id} tab={tab} />
					</Suspense>
				</ErrorBoundary>
			)}
		</PageContainer>
	);
}

function FichaPanel({
	person,
	tab,
	isDoctor,
}: {
	person: PersonDetail;
	tab: FichaTab;
	isDoctor: boolean;
}) {
	if (tab === "conversa" && person.sendpulse_id !== null) {
		return <FichaConversation person={person} />;
	}

	return <FichaCadastro person={person} isDoctor={isDoctor} />;
}

function FichaData({ id, tab }: { id: number; tab: FichaTab }) {
	const { data } = useSuspenseQuery(orpc.people.detail.queryOptions({ input: { id } }));
	const { data: me } = useSuspenseQuery(orpc.auth.me.queryOptions());

	if (data === null) {
		return <p className="text-sm text-muted-foreground">Pessoa #{id} não encontrada.</p>;
	}

	const isDoctor = me.role === "medical";

	return (
		<div className="flex flex-col gap-5 fade-up">
			<div className="workspace-frame overflow-hidden">
				<FichaHeader person={data} compact={tab === "conversa" && data.sendpulse_id !== null} />

				<FichaTabsBar id={id} tab={tab} hasChannel={data.sendpulse_id !== null} />
			</div>

			<FichaPanel person={data} tab={tab} isDoctor={isDoctor} />
		</div>
	);
}

function FichaError({ id }: { id: number }) {
	return <p className="text-sm text-destructive">Não foi possível carregar a pessoa #{id}.</p>;
}
